import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { serviceOrderFilters } from "../../types";
import { useAreaConfig, useServiceAssociations } from "../../hooks";

interface ServiceOrderFilterPanelProps {
  currentFilters: Partial<serviceOrderFilters>;
  onApplyFilters: (filters: Partial<serviceOrderFilters>) => void;
  onResetFilters: () => void;
  orderType?: "pending" | "assigned" | "completed" | "history";
}

export const ServiceOrderFilterPanel: React.FC<
  ServiceOrderFilterPanelProps
> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
  orderType = "pending",
}) => {
  const { data: areaOptions } = useAreaConfig();
  const { data: association } = useServiceAssociations();

  const form = useForm({
    defaultValues: {
      status: currentFilters.status || "all",
      serviceAssociation: currentFilters.serviceAssociation || "all",
      priority: currentFilters.priority || "all",
      area: currentFilters.area || "all",
      slaFilter: currentFilters.slaFilter || "all",
    },
  });

  useEffect(() => {
    // Ensure we always use "all" as fallback, never undefined
    form.setValue("status", currentFilters.status || "all");
    form.setValue(
      "serviceAssociation",
      currentFilters.serviceAssociation || "all"
    );
    form.setValue("priority", currentFilters.priority || "all");
    form.setValue("area", currentFilters.area || "all");
    form.setValue("slaFilter", currentFilters.slaFilter || "all");
  }, [currentFilters, form]);

  const handleApplyFilters = (data: any) => {
    const filtersToApply: Partial<serviceOrderFilters> = {};

    if (data.status && data.status !== "all") {
      filtersToApply.status = data.status;
    }

    if (data.serviceAssociation && data.serviceAssociation !== "all") {
      filtersToApply.serviceAssociation = data.serviceAssociation;
    }

    if (data.priority && data.priority !== "all") {
      filtersToApply.priority = data.priority;
    }

    if (data.area && data.area !== "all") {
      filtersToApply.area = data.area;
    }

    if (data.slaFilter && data.slaFilter !== "all") {
      filtersToApply.slaFilter = data.slaFilter;
    }

    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    form.reset({
      status: "all",
      serviceAssociation: "all",
      priority: "all",
      area: "all",
      slaFilter: "all",
    });
    onResetFilters();
  };

  // Status options based on order type - FIXED: consistent casing
  const getStatusOptions = () => {
    const baseOptions = [{ value: "all", label: "All Status" }];

    switch (orderType) {
      case "pending":
        return [
          ...baseOptions,
          { value: "CREATED", label: "Created" },
          { value: "OVERDUE", label: "Overdue" },
          { value: "REFUSED", label: "refused" },
        ];
      case "assigned":
        return [
          ...baseOptions,
          { value: "ASSIGNED", label: "Assigned" },
          { value: "ACCEPTED", label: "Accepted" },
          { value: "IN_PROGRESS", label: "In Progress" },
        ];
      case "completed":
        return [...baseOptions, { value: "COMPLETED", label: "Completed" }];
      case "history":
        return [
          ...baseOptions,
          { value: "APPROVED", label: "Approved" },
          { value: "REJECTED", label: "Rejected" },
        ];
      default:
        return baseOptions;
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleApplyFilters)}>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        <div className="space-y-2">
          <Label>Status</Label>
          <Controller
            name="status"
            control={form.control}
            render={({ field }) => {
              return (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue
                      placeholder="Select status"
                      className={
                        field.value === "all"
                          ? "text-gray-500"
                          : "text-gray-900"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {getStatusOptions().map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value} // FIXED: removed .toUpperCase()
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              );
            }}
          />
        </div>

        <div className="space-y-2">
          <Label>Service Association</Label>
          <Controller
            name="serviceAssociation"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue
                    placeholder="Select association"
                    className={
                      field.value === "all" ? "text-gray-500" : "text-gray-900"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All Association</SelectItem>
                    {association?.result.map((assoc) => (
                      <SelectItem
                        key={assoc.key}
                        value={assoc.value.toUpperCase()}
                      >
                        {assoc.value.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>Priority</Label>
          <Controller
            name="priority"
            control={form.control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue
                    placeholder="Select priority"
                    className={
                      field.value === "all" ? "text-gray-500" : "text-gray-900"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="CRITICAL">Critical</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label>Area</Label>
          <Controller
            name="area"
            control={form.control}
            render={({ field }) => {
              return (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue
                      placeholder="Select area"
                      className={
                        field.value === "all"
                          ? "text-gray-500"
                          : "text-gray-900"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">All Areas</SelectItem>
                      {areaOptions?.map((area) => (
                        <SelectItem key={area.code} value={area.code}>
                          {area.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              );
            }}
          />
        </div>

        {/* SLA Filter - show only for completed and history */}
        {(orderType === "completed" || orderType === "history") && (
          <div className="space-y-2">
            <Label>SLA Compliance</Label>
            <Controller
              name="slaFilter"
              control={form.control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue
                      placeholder="Select SLA"
                      className={
                        field.value === "all"
                          ? "text-gray-500"
                          : "text-gray-900"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">All SLA Status</SelectItem>{" "}
                      {/* FIXED: changed from "ALL" to "all" */}
                      <SelectItem value="WITHIN">Within SLA</SelectItem>
                      <SelectItem value="OVERDUE">Overdue</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        )}

        <div className="col-span-1 md:col-span-3 lg:col-span-5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleResetFilters}>
            Reset Filters
          </Button>
          <Button type="submit">Apply Filters</Button>
        </div>
      </div>
    </form>
  );
};
